import { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';

import { ParsedUrlQuery } from 'querystring';

import { datoApi, markets } from '@hultafors/hultaforsgroup/api';
import {
  BrandPageContentFragment,
  GlobalFields,
  PageProps,
} from '@hultafors/hultaforsgroup/types';
import {
  cacheControl,
  filterMarkets,
  getCanonicalUrl,
  getHrefLanguages,
} from '@hultafors/shared/helpers';

import { prodDomains } from '../../next.domains';

const BrandPageBlock = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then(
    (module) => module.BrandPageBlock,
  ),
);
const Hero = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then((module) => module.Hero),
);
const Page = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then((module) => module.Page),
);

interface BrandPageParams extends ParsedUrlQuery {
  slug: string;
}

type BrandPageProps = PageProps<BrandPageContentFragment>;

export const getServerSideProps: GetServerSideProps<
  BrandPageProps,
  BrandPageParams
> = async ({ locale, params: { slug }, res }) => {
  try {
    const dato = datoApi(locale);
    const path = `/our-brands/${slug}`;
    const canonical = getCanonicalUrl(
      locale,
      prodDomains,
      'www.hultaforsgroup.com',
      path,
    );
    const {
      data: { brandPage: content, ...globalFields },
    } = await dato.getBrandPage(slug);
    if (!content || content === null) {
      return {
        notFound: true,
      };
    }

    const hrefLanguages = getHrefLanguages(
      null,
      filterMarkets(markets, 'HULTAFORSGROUP'),
      content?.pageLocales,
    );

    const props: BrandPageProps = {
      content,
      ...(globalFields as unknown as GlobalFields),
      canonical,
      hrefLanguages,
    };

    res.setHeader('Cache-Control', cacheControl.page);
    return { props };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

const BrandPage: NextPage<BrandPageProps> = ({
  canonical,
  content,
  hrefLanguages,
}) => {
  return (
    <Page canonical={canonical} hrefLanguages={hrefLanguages} seo={content.seo}>
      <Hero image={content?.heroImage} priority title={content.heroTitle} />
      <BrandPageBlock
        linkText={content.linkToWebsiteText}
        linkUrl={content.linkToWebsite}
        logo={content.logo}
        text={content.description}
        title={content.brandName}
      />
    </Page>
  );
};

export default BrandPage;
